import { friendInterestText } from './community';
import { CyclingEvent } from './types';
import { eventCardModel } from './eventCard';

/** Gemeinsame, reduzierte Agenda-Daten für Web und App. */
export function plannerEventModel(event: CyclingEvent) {
  const card = eventCardModel(event);
  const date = event.eventDate ? new Date(`${event.eventDate.slice(0, 10)}T12:00:00`) : null;
  const validDate = !event.permanent && date && !Number.isNaN(date.getTime()) ? date : null;
  const month = validDate ? validDate.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' }) : 'Jederzeit';
  const friendLabel = friendInterestText(event.friendInterests ?? []);
  const distances = (event.distances ?? []).filter(d => d.distanceKm > 0 && !d.durationMinutes).map(d => d.distanceKm);
  const distanceLabel = distances.length && distances.length === event.distances.length
    ? (Math.min(...distances) === Math.max(...distances) ? `${distances[0]} km` : `${Math.min(...distances)}–${Math.max(...distances)} km`)
    : [...card.chips, card.more].filter(Boolean).join(' / ');
  return {
    month, date: card.date, permanentLabel: card.permanentLabel,
    location: event.city || card.region,
    categoryLabel: card.categoryLabel, distanceLabel,
    details: [card.categoryLabel, distanceLabel].filter(Boolean).join(' · '),
    status: card.status, cancelled: card.cancelled, friendLabel,
    participationLabel: event.participating ? 'Dabei' : event.favorited ? 'Vorgemerkt' : 'Teilnahme',
  };
}
export function groupPlannerEvents(events: CyclingEvent[]) {
  const groups: { month: string; events: CyclingEvent[] }[] = [];
  for (const event of events) {
    const month = plannerEventModel(event).month;
    const last = groups[groups.length - 1];
    if (last?.month === month) last.events.push(event);
    else groups.push({ month, events: [event] });
  }
  return groups;
}

/**
 * Kopf des Profils (30.10.2026, Web; die App kann dieselbe Ableitung nutzen): wie viele Events
 * vorgemerkt sind, bei wie vielen man dabei ist, wie viele Freunde und offene Anfragen es gibt,
 * und welches Event als Nächstes ansteht. Abgesagte und vergangene zählen nicht als „nächstes“.
 */
export function profilUebersicht(
  events: CyclingEvent[],
  people: { state: string }[],
  today: Date = new Date(),
) {
  const tag = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const kommend = events
    .filter(e => !e.permanent && e.eventDate && new Date(`${(e.endDate ?? e.eventDate).slice(0, 10)}T00:00:00`).getTime() >= tag)
    .sort((a, b) => a.eventDate.localeCompare(b.eventDate));
  return {
    vorgemerkt: events.filter(e => e.favorited).length,
    dabei: events.filter(e => e.participating).length,
    freunde: people.filter(p => p.state === 'FRIEND').length,
    anfragen: people.filter(p => p.state === 'INCOMING').length,
    naechstes: kommend.find(e => !e.cancelled) ?? null,
  };
}

/**
 * Abstand zwischen zwei Events in Meine Events (30.10.2026): vom Ende des einen bis zum Start
 * des nächsten, für die Vorbereitung. Ab zwei Wochen zusätzlich in Wochen, weil Trainingspläne
 * in Wochen rechnen. `null`, wenn eins der beiden keinen Termin hat (jederzeit fahrbar).
 */
export function plannerAbstand(vorher: CyclingEvent, nachher: CyclingEvent): { tage: number; label: string } | null {
  if (vorher.permanent || nachher.permanent || !vorher.eventDate || !nachher.eventDate) return null;
  const tag = (d: string) => Date.UTC(+d.slice(0, 4), +d.slice(5, 7) - 1, +d.slice(8, 10));
  const tage = Math.round((tag(nachher.eventDate) - tag((vorher.endDate ?? vorher.eventDate))) / 86400000);
  if (tage <= 0) return { tage, label: 'gleichzeitig' };
  if (tage === 1) return { tage, label: '1 Tag' };
  if (tage < 14) return { tage, label: `${tage} Tage` };
  return { tage, label: `${tage} Tage · ${Math.floor(tage / 7)} Wochen` };
}
